import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

// Simplified Profile interface
interface Profile {
  id: string;  // uuid in postgres = string in TypeScript
  email: string;  // text in postgres = string in TypeScript
  created_at: string;
  updated_at: string;
  pinned_menu_paths?: string[];
  is_active?: boolean;
  role?: 'viewer' | 'editor' | 'admin_level_2' | 'admin_level_3' | 'admin' | 'member';
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchProfile = async (userId: string, email: string): Promise<Profile | null> => {
    try {
      // First try to get existing profile
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (existingProfile) {
        return existingProfile as Profile;
      }

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching profile:', fetchError);
        return null;
      }

      // Create new profile if none exists
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert([{ id: userId, email }])
        .select()
        .single();

      if (createError) {
        console.error('Error creating profile:', createError);
        return null;
      }

      return newProfile as Profile;
    } catch (error) {
      console.error('Profile fetch/create error:', error);
      return null;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setProfile(null);
      setUser(null);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          const profileData = await fetchProfile(session.user.id, session.user.email!);
          if (mounted && profileData) {
            setProfile(profileData);
          }
        }
      } catch (error) {
        console.error('Initialization error:', error);
      } finally {
        if (mounted) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only log once
      if (event !== 'INITIAL_SESSION') {
        console.log('Auth state change:', event, session?.user?.email);
      }
      
      if (!mounted || !isInitialized) return;

      if (session?.user) {
        // Set loading=true before the async profile fetch so RequireAuth shows
        // a spinner instead of redirecting while the profile is still pending.
        setLoading(true);
        setUser(session.user);
        // Check if we need to fetch a profile - only if user ID changed or no profile
        const needProfileFetch = !profile || profile.id !== session.user.id;

        if (needProfileFetch) {
          const profileData = await fetchProfile(session.user.id, session.user.email!);
          if (mounted && profileData) {
            setProfile(profileData);
          }
        }
      } else {
        setUser(null);
        setProfile(null);
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [isInitialized]);

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
